import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";

var _dec, _dec2, _dec3, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { View } from '@instructure/ui-view';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { omitProps, deprecated } from '@instructure/ui-react-utils';
import { testable } from '@instructure/ui-testable';
var styles = {
  componentId: 'eQCNs',
  template: function template(theme) {
    return "\n\n.eQCNs_bGBk{border-collapse:collapse;border-spacing:0;color:".concat(theme.color || 'inherit', ";display:table;font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.fontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";max-width:100%;width:100%}\n\n.eQCNs_bGBk,.eQCNs_bGBk caption{background:").concat(theme.background || 'inherit', "}\n\n.eQCNs_bGBk caption{color:").concat(theme.captionColor || 'inherit', ";font-size:").concat(theme.captionFontSize || 'inherit', ";text-align:start}\n\n[dir=ltr] .eQCNs_bGBk caption{text-align:left}\n\n[dir=rtl] .eQCNs_bGBk caption{text-align:right}\n\n.eQCNs_bGBk td,.eQCNs_bGBk th,.eQCNs_bGBk tr{box-sizing:border-box}\n\n.eQCNs_bGBk td,.eQCNs_bGBk th{border-bottom-color:").concat(theme.borderColor || 'inherit', ";border-bottom-style:solid;border-bottom-width:0.0625rem;text-align:start;vertical-align:middle}\n\n[dir=ltr] .eQCNs_bGBk td,[dir=ltr] .eQCNs_bGBk th{text-align:left}\n\n[dir=rtl] .eQCNs_bGBk td,[dir=rtl] .eQCNs_bGBk th{text-align:right}\n\n.eQCNs_bGBk th{font-weight:700}\n\n.eQCNs_bGBk thead>th{border-bottom-color:").concat(theme.headerBorderColor || 'inherit', "}\n\n.eQCNs_doqw{font-size:").concat(theme.smallFontSize || 'inherit', "}\n\n.eQCNs_doqw td,.eQCNs_doqw th{line-height:").concat(theme.smallLineHeight || 'inherit', ";padding:").concat(theme.smallPadding || 'inherit', "}\n\n.eQCNs_ycrn{font-size:").concat(theme.mediumFontSize || 'inherit', "}\n\n.eQCNs_ycrn td,.eQCNs_ycrn th{line-height:").concat(theme.mediumLineHeight || 'inherit', ";padding:").concat(theme.mediumPadding || 'inherit', "}\n\n.eQCNs_cMDj{font-size:").concat(theme.largeFontSize || 'inherit', "}\n\n.eQCNs_cMDj td,.eQCNs_cMDj th{line-height:").concat(theme.largeLineHeight || 'inherit', ";padding:").concat(theme.largePadding || 'inherit', "}\n\n.eQCNs_bBWt>tbody>tr>td:nth-child(2n),.eQCNs_cWEh>tbody>tr:nth-child(odd){background-color:").concat(theme.stripedBackground || 'inherit', "}\n\n.eQCNs_fKeI>tbody>tr{border-left:0.1875rem solid transparent;border-right:0.1875rem solid transparent}\n\n.eQCNs_fKeI>tbody>tr:hover{border-color:").concat(theme.hoverBorderColor || 'inherit', "}\n\n.eQCNs_bvTK{table-layout:fixed}");
  },
  'root': 'eQCNs_bGBk',
  'small': 'eQCNs_doqw',
  'medium': 'eQCNs_ycrn',
  'large': 'eQCNs_cMDj',
  'colStriped': 'eQCNs_bBWt',
  'rowStriped': 'eQCNs_cWEh',
  'hover': 'eQCNs_fKeI',
  'fixedLayout': 'eQCNs_bvTK'
};
import theme from './theme';
/**
---
category: components/deprecated
id: DeprecatedTable
---
**/

var Table = (_dec = deprecated('7.0.0', null, 'Use @instructure/ui-table instead.'), _dec2 = testable(), _dec3 = themeable(theme, styles), _dec(_class = _dec2(_class = _dec3(_class = (_temp = _class2 =
/*#__PURE__*/
function (_Component) {
  _inherits(Table, _Component);

  function Table() {
    _classCallCheck(this, Table);

    return _possibleConstructorReturn(this, _getPrototypeOf(Table).apply(this, arguments));
  }

  _createClass(Table, [{
    key: "render",
    value: function render() {
      var _classes;

      var classes = (_classes = {}, _defineProperty(_classes, styles.root, true), _defineProperty(_classes, styles.fixedLayout, this.props.layout === 'fixed'), _defineProperty(_classes, styles[this.props.size], true), _defineProperty(_classes, styles.rowStriped, this.props.striped === 'rows'), _defineProperty(_classes, styles.colStriped, this.props.striped === 'columns'), _defineProperty(_classes, styles.hover, this.props.hover), _classes);
      var passthroughProps = View.omitViewProps(omitProps(this.props, Table.propTypes), Table);
      return React.createElement(View, Object.assign({}, passthroughProps, {
        elementRef: this.props.elementRef,
        as: "table",
        className: classnames(classes),
        margin: this.props.margin
      }), React.createElement("caption", null, this.props.caption), this.props.children);
    }
  }]);

  Table.displayName = "Table";
  return Table;
}(Component), _class2.propTypes = {
  /**
  * Set the table's caption element (its label)
  */
  caption: PropTypes.node.isRequired,

  /**
  * Build the table markup via the children prop
  */
  children: PropTypes.node,

  /**
  * Highlight each row on hover
  */
  hover: PropTypes.bool,

  /**
  * Controls the padding and font-size of table cells
  */
  size: PropTypes.oneOf(['small', 'medium', 'large']),

  /**
  * Add a light background color to alternate rows or columns
  */
  striped: PropTypes.oneOf(['columns', 'rows']),

  /**
  * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
  * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
  * familiar CSS-like shorthand. For example: `margin="small auto large"`.
  */
  margin: ThemeablePropTypes.spacing,

  /**
  * provides a reference to the underlying html element
  */
  elementRef: PropTypes.func,

  /**
  * `auto` lets the browser determine table column widths based on cell content,
  * while `fixed` forces columns of equal width.
  */
  layout: PropTypes.oneOf(['auto', 'fixed'])
}, _class2.defaultProps = {
  children: null,
  striped: void 0,
  margin: void 0,
  elementRef: void 0,
  hover: false,
  size: 'medium',
  layout: 'auto'
}, _temp)) || _class) || _class) || _class);
export default Table;
export { Table };